/**
 * Session Management Hook
 * Handles automatic session restoration, token refresh, and activity monitoring
 */

import { useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { useAppDispatch } from '../store/hooks';
import { 
  checkStoredSession, 
  autoRefreshToken, 
  clearAuth, 
  updateLastActivity 
} from '../store/slices/authSlice';
import { authPersistence } from '../services/authPersistence';
import { logError } from '../utils/errorLogger';

export interface UseSessionManagementOptions {
  autoCheckSession?: boolean;
  autoRefresh?: boolean;
  activityMonitoring?: boolean;
  onSessionExpired?: () => void;
  onTokenRefreshed?: () => void;
}

export function useSessionManagement(options: UseSessionManagementOptions = {}) {
  const {
    autoCheckSession = true,
    autoRefresh = true,
    activityMonitoring = true,
    onSessionExpired,
    onTokenRefreshed,
  } = options;

  const dispatch = useAppDispatch();
  const { 
    isAuthenticated, 
    sessionChecked, 
    tokens, 
    user,
    rememberMe 
  } = useSelector((state: RootState) => state.auth);

  /**
   * Initialize session on app startup
   */
  const initializeSession = useCallback(async () => {
    if (!autoCheckSession || sessionChecked) return;

    try {
      await dispatch(checkStoredSession()).unwrap();
    } catch (error: unknown) {
      // No valid session to restore
    }
  }, [dispatch, autoCheckSession, sessionChecked]);

  /**
   * Handle session expiration
   */
  const handleSessionExpired = useCallback(() => {
    dispatch(clearAuth());
    authPersistence.clearAuthData();
    onSessionExpired?.();
  }, [dispatch, onSessionExpired]);

  /**
   * Handle token refresh
   */
  const handleTokenRefresh = useCallback(async () => {
    if (!autoRefresh || !isAuthenticated) return;

    try {
      await dispatch(autoRefreshToken()).unwrap();
      onTokenRefreshed?.();
    } catch (error: unknown) {
      logError('Token refresh failed', error);
      handleSessionExpired();
    }
  }, [dispatch, autoRefresh, isAuthenticated, onTokenRefreshed, handleSessionExpired]);

  /**
   * Update user activity
   */
  const updateActivity = useCallback(() => {
    if (isAuthenticated && activityMonitoring) {
      dispatch(updateLastActivity());
    }
  }, [dispatch, isAuthenticated, activityMonitoring]);

  /**
   * Manually refresh token
   */
  const refreshToken = useCallback(async () => {
    if (!isAuthenticated) return false;

    try {
      await dispatch(autoRefreshToken()).unwrap();
      return true;
    } catch (error: unknown) {
      logError('Manual token refresh failed', error);
      return false;
    }
  }, [dispatch, isAuthenticated]);

  /**
   * Check if session is still valid
   */
  const isSessionValid = useCallback(() => {
    return authPersistence.isSessionValid();
  }, []);

  /**
   * Force logout
   */
  const forceLogout = useCallback(() => {
    dispatch(clearAuth());
    authPersistence.clearAuthData();
  }, [dispatch]);

  /**
   * Get session info
   */
  const getSessionInfo = useCallback(() => {
    return {
      isAuthenticated,
      user,
      rememberMe,
      sessionChecked,
      tokens,
      authStatus: authPersistence.getAuthStatus(),
    };
  }, [isAuthenticated, user, rememberMe, sessionChecked, tokens]);

  // Initialize session on mount
  useEffect(() => {
    initializeSession();
  }, [initializeSession]);

  // Set up session monitoring
  useEffect(() => {
    if (!isAuthenticated || !autoRefresh) return;

    const cleanup = authPersistence.initializeSessionMonitoring(
      handleSessionExpired,
      handleTokenRefresh
    );

    return cleanup;
  }, [isAuthenticated, autoRefresh, handleSessionExpired, handleTokenRefresh]);

  // Set up activity monitoring
  useEffect(() => {
    if (!activityMonitoring || !isAuthenticated) return;

    const activityEvents = [
      'mousedown', 
      'mousemove', 
      'keypress', 
      'scroll', 
      'touchstart', 
      'click'
    ];

    const handleActivity = () => updateActivity();

    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [activityMonitoring, isAuthenticated, updateActivity]);

  return {
    // State
    isAuthenticated,
    sessionChecked,
    user,
    rememberMe,
    
    // Actions
    refreshToken,
    forceLogout,
    updateActivity,
    
    // Utils
    isSessionValid,
    getSessionInfo,
    initializeSession,
  };
}

export default useSessionManagement;
